import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { ToastManagerProp, withToastManager } from 'react-toast-notifications';
import { CSSTransition } from 'react-transition-group';

import cn from 'classnames';
import CourseConfirm from 'components/Course/CourseConfirm';
import CourseImport from 'components/Course/CourseImport';
import CourseInfo from 'components/Course/CourseInfo';
import CourseSchedule from 'components/Course/CourseSchedule';
import CourseTimeline from 'components/Course/CourseTimeline';
import { Button, Loader } from 'components/UtilElements';
import { apiErrorHandler } from 'core/errors';
import { CourseImportScreen } from 'hooks/use-course-import';
import { Dispatch } from 'redux';
import { UPDATE_COURSE_DETAILS, UPDATE_SELECTED_CHILD_COURSE_ID } from 'redux/actions';
import { API, getCancelToken, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

interface GetCourseDetailsResponse extends CourseDetails, MessageResponse {}

interface CourseState {
  isSavingSchedule: boolean;
}

interface RouteState {
  courseId: string;
}

interface CourseProps extends ToastManagerProp, RouteComponentProps<never, never, RouteState> {
  className?: string;
  activeScreen: CourseImportScreen;
  goto: (screen: Exclude<CourseImportScreen, 'loading'>) => void;
  onPrev: () => void;
  onNext: () => void;
  course: CourseDetails | null;
  getAccess: SessionData | null;
  selectedChildCourseId: string | null;
  allCourses: AvailableCourse[];
  updateCourse(course: CourseDetails): Promise<void>;
  updateSelectedChildCourseId: (childCourseId: string | null) => void;
}

class Course extends Component<CourseProps, CourseState> {
  apiSources = getCancelToken();

  state: CourseState = {
    isSavingSchedule: false,
  };

  setScreen = (status: CourseStatus) => {
    if (status.infoSet === 0) {
      this.props.goto('course-info');
    } else if (status.scheduleSet === 0) {
      this.props.goto('course-schedule');
    } else if (status.studentsEnrolled === 0) {
      this.props.goto('course-import');
    } else {
      this.props.goto('course-confirm');
    }
  };

  getCourseDetails(id: string) {
    return request<GetCourseDetailsResponse>({
      url: API.GET_COURSE_DETAILS(id, this.props.selectedChildCourseId ?? undefined),
      cancelToken: this.apiSources.token,
    });
  }

  private initializeCourse(course: CourseDetails) {
    const { isAdminSet, instructors } = course.team;
    const { courseTimezone } = course.dates;

    /**
     * if there is only one instructor and admin is not set
     * then make that single instructor as admin by default
     */
    if (!isAdminSet && instructors.length === 1 && instructors[0].valid) {
      const { userId, name, emailId, hasAcadlyAccount, valid, invalidReason } = instructors[0];
      course.team.isAdminSet = 1;
      course.team.instructors = [];
      course.team.admin = {
        userId,
        emailId,
        hasAcadlyAccount,
        invalidReason,
        name,
        valid,
      };
    }

    /**
     * if there is only one course timezone available
     * and timezone is not set then select it by default
     */
    if (!courseTimezone.valid && courseTimezone.available.length === 1) {
      courseTimezone.valid = 1;
      courseTimezone.value = courseTimezone.available[0];
    }

    this.props.updateCourse(course);
    this.setScreen(course.status);
  }

  async componentDidMount() {
    const sessionData = this.props.getAccess;
    const context = sessionData?.context;

    this.apiSources = getCancelToken();

    try {
      const courseId =
        context === 'course' ? sessionData?.courseDetails.courseId : this.props.location.state.courseId;

      if (courseId) {
        const { message: _message, ...course } = await this.getCourseDetails(courseId);
        this.initializeCourse(course);
      } else {
        throw new Error('courseId is not provided!');
      }
    } catch (err: any) {
      apiErrorHandler(this.props.toastManager, err);
    }
  }

  componentWillUnmount() {
    this.apiSources.cancel();
    if (this.props.getAccess?.context === 'organization') {
      this.props.updateSelectedChildCourseId(null);
    }
  }

  getScreen = () => {
    const { activeScreen, onPrev, onNext } = this.props;

    switch (activeScreen) {
      case 'loading':
        return <Loader />;
      case 'course-info':
        return <CourseInfo onNext={onNext} />;
      case 'course-schedule':
        return <CourseSchedule onPrev={onPrev} onNext={onNext} />;
      case 'course-timeline':
        return <CourseTimeline onPrev={onPrev} onNext={onNext} />;
      case 'course-import':
        return <CourseImport onPrev={onPrev} onNext={onNext} />;
      case 'course-confirm':
        return <CourseConfirm onPrev={onPrev} />;
      default:
        return null;
    }
  };

  render() {
    return (
      <div className={cn('import-course', this.props.className)}>
        {this.props.getAccess?.context === 'organization' &&
          this.props.allCourses.find((course) => course.courseId === this.props.location.state.courseId)
            ?.hasChildren === 1 && (
            <Link
              to={{
                pathname: ROUTES.ADMIN_COURSES_SELECT,
                state: { courseId: this.props.location.state.courseId },
              }}
              component={({ navigate, href }) => {
                return (
                  <Button
                    variant='outlined'
                    color='info'
                    size='small'
                    className='import-course__action import-course__close'
                    onClick={() => {
                      navigate(href);
                    }}
                  >
                    CLOSE
                  </Button>
                );
              }}
            />
          )}
        <h1 className='import-course__header'>Importing course into Acadly</h1>
        <CSSTransition className='fade' timeout={300}>
          {this.getScreen()}
        </CSSTransition>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  course: state.courseInCreation,
  getAccess: state.getAccess,
  allCourses: state.allCourses,
  selectedChildCourseId: state.selectedChildCourseId,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCourse: async (course: CourseDetails) => {
    dispatch({
      type: UPDATE_COURSE_DETAILS,
      payload: course,
    });
  },
  updateSelectedChildCourseId: (childCourseId: string | null) => {
    dispatch({
      type: UPDATE_SELECTED_CHILD_COURSE_ID,
      payload: childCourseId,
    });
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withToastManager(Course)));
